import React from 'react';

interface PollWithBackoffOptions {
  initialDelayMs?: number;
  maxDelayMs?: number;
}

// Calls `action` after initialDelayMs, then after doubling delays, and gives up once the next delay would exceed
// maxDelayMs. Polls while `runKey` is truthy; a changed `runKey` starts a fresh run, a re-render does not.
export const usePollWithBackoff = (
  action: () => unknown,
  runKey: string | null | undefined,
  { initialDelayMs = 5000, maxDelayMs = 45 * 60 * 1000 }: PollWithBackoffOptions = {}
): void => {
  const actionRef = React.useRef(action);
  actionRef.current = action;

  React.useEffect(() => {
    let delay = initialDelayMs;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    let isCancelled = false;

    const scheduleNextPoll = () => {
      timeoutId = setTimeout(async () => {
        try {
          await actionRef.current();
        } finally {
          delay *= 2;
          if (!isCancelled && delay <= maxDelayMs) {
            scheduleNextPoll();
          }
        }
      }, delay);
    };

    if (runKey) {
      scheduleNextPoll();
    }

    return () => {
      isCancelled = true;
      clearTimeout(timeoutId);
    };
  }, [runKey, initialDelayMs, maxDelayMs]);
};
